import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import WizardStepper from '../UnifiedWizard/WizardStepper';
import LandingPage from '../LandingPage';
import AssistantDefination from '../AssistantDefination';
import Personalization from '../Personalization';
import InitialAnalytics from '../InitialAnalytics';
import { useSaveAssistant } from '../../../hooks/useSaveAssistant';
import { useUser } from '../../../hooks/useUser';
import { useAgents } from '../../../hooks/useAgents';

const steps = [
  { label: 'Template', emoji: '🧩' },
  { label: 'Definition', emoji: '📝' },
  { label: 'Personalization', emoji: '✨' },
  { label: 'Analytics', emoji: '📊' },
];

const AgentCreationWizardFlow = () => {
  const { user } = useUser();
  const { agents } = useAgents();
  const { saveAssistant, isPending } = useSaveAssistant();

  const [currentStep, setCurrentStep] = useState(0);

  const definitionSubSection = 'assistant-name';
  const personalizationSubSection = 'greeting';

  const assistantId = useMemo(() => {
    const list = agents?.agents ?? [];
    return list.length ? list[list.length - 1]._id : '';
  }, [agents?.agents]);

  const completedSteps = useMemo(() => {
    const set = new Set<number>();
    for (let i = 0; i < currentStep; i += 1) set.add(i);
    return set;
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(Math.max(0, Math.min(step, steps.length - 1)));
    },
    [setCurrentStep]
  );

  const handleTemplateSelect = useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  const saveDraftSection = useCallback(
    async (section: 'assistant-definition' | 'personalization') => {
      if (!user?._id || !assistantId) return;
      await saveAssistant({
        userId: user._id,
        assistantId,
        businessId: user?.business?._id,
        status: 'draft',
        section,
      });
    },
    [assistantId, saveAssistant, user?._id, user?.business?._id]
  );

  const handleDefinitionNext = useCallback(async () => {
    await saveDraftSection('assistant-definition');
    goToStep(2);
  }, [goToStep, saveDraftSection]);

  const handlePersonalizationNext = useCallback(async () => {
    await saveDraftSection('personalization');
    goToStep(3);
  }, [goToStep, saveDraftSection]);

  const handleBackFromDefinition = useCallback(() => {
    goToStep(0);
  }, [goToStep]);

  const handleBackFromPersonalization = useCallback(() => {
    goToStep(1);
  }, [goToStep]);

  const handleBackFromAnalytics = useCallback(() => {
    goToStep(2);
  }, [goToStep]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'secondary.main',
      }}
    >
      <WizardStepper
        currentStep={currentStep}
        completedSteps={completedSteps}
        steps={steps}
        dimUpcoming={false}
      />

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {currentStep === 0 && (
          <LandingPage onTemplateSelect={handleTemplateSelect} embedded />
        )}

        {currentStep === 1 && (
          <AssistantDefination
            selectedSubSection={definitionSubSection}
            onNext={() => void handleDefinitionNext()}
            onPrev={handleBackFromDefinition}
            isPending={isPending}
            shouldShowMenuConfigFlow={true}
          />
        )}

        {currentStep === 2 && (
          <Personalization
            selectedSubSection={personalizationSubSection}
            onNext={() => void handlePersonalizationNext()}
            onPrev={handleBackFromPersonalization}
            isPending={isPending}
          />
        )}

        {currentStep === 3 && (
          <InitialAnalytics
            onPrev={handleBackFromAnalytics}
            shouldShowMenuConfigFlow={false}
          />
        )}
      </Box>
    </Box>
  );
};

export default AgentCreationWizardFlow;
